import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type {
  AddMemberRoleFn,
  AddTeamMemberFn,
  BulkCreateCustomersFn,
  CreateCustomerFn,
  CreateMemberFn,
  CreateMemberManuallyFn,
  FetchMemberFn,
  ListRolesFn,
  FetchSearchableUserFn,
  FetchUserDataFn,
  ListUserTeamMembersForWorkspaceFn,
  ListTeamsFn,
  GenerateMemberMagicLinkFn,
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetSearchableUsersFn,
  GetWorkspaceRolesFn,
  GetWorkspaceSelectionFn,
  GetWorkspaceSkillsFn,
  GetWorkspaceTeamRolesFn,
  GetWorkspaceTeamsFn,
  ImpersonateMemberFn,
  ListSkillsFn,
  ListTeamRolesFn,
  ListWorkspacesFn,
  PlatformAdminWorkspaceFn,
  PrimeProviderFn,
  ProDetailsViewModelFactory,
  SearchMembersFn,
  ListTeamMembersForWorkspaceMemberFn,
  SearchUsersFn,
  UpdateMemberFn,
  UpdateMemberSkillsFn,
  UpdateUserDataFn,
} from '@/features/iam'
import type {
  FetchBillingProfileFn,
  FetchMemberCouponsFn,
} from '@/features/billing'
import type { DocumentsTabViewModelFactory } from '@/features/documents'
import type {
  AdvisorClientsTabViewModelFactory,
  ClientAssignmentViewModelFactory,
} from '@/features/client-user-management'
import type {
  FetchJobTotalStatusAggregationsFn,
  FetchJobTotalsFn,
  PromptCreateJobFn,
  UserJobsTabViewModelFactory,
} from '@/features/jobs'
import type { FetchPayoutMethodInfoFn } from '@/features/payouts'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'

/**
 * Options for {@link UserManagementFeature}.
 */
export interface UserManagementFeatureOptions {
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  getCurrentMember: GetCurrentMemberFn
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn
  getWorkspaceSelection: GetWorkspaceSelectionFn
  listWorkspaces: ListWorkspacesFn
  joinWorkspaceAsAdmin: PlatformAdminWorkspaceFn
  promoteToOwner: PlatformAdminWorkspaceFn
  searchUsers: SearchUsersFn
  fetchSearchableUser: FetchSearchableUserFn
  getSearchableUsers: GetSearchableUsersFn
  fetchMember: FetchMemberFn
  createMember: CreateMemberFn
  createMemberManually: CreateMemberManuallyFn
  searchMembersByPermission: SearchMembersFn
  updateMember: UpdateMemberFn
  addMemberRole: AddMemberRoleFn
  addMemberSkills: UpdateMemberSkillsFn
  removeMemberSkills: UpdateMemberSkillsFn
  impersonateMember: ImpersonateMemberFn
  generateMemberMagicLink: GenerateMemberMagicLinkFn
  listRoles: ListRolesFn
  getWorkspaceRoles: GetWorkspaceRolesFn
  listSkills: ListSkillsFn
  getWorkspaceSkills: GetWorkspaceSkillsFn
  listTeams: ListTeamsFn
  getWorkspaceTeams: GetWorkspaceTeamsFn
  addTeamMember: AddTeamMemberFn
  listTeamMembersForWorkspaceMember: ListTeamMembersForWorkspaceMemberFn
  listUserTeamMembersForWorkspace: ListUserTeamMembersForWorkspaceFn
  listTeamRoles: ListTeamRolesFn
  getWorkspaceTeamRoles: GetWorkspaceTeamRolesFn
  fetchUserData: FetchUserDataFn
  updateUserData: UpdateUserDataFn
  createCustomer: CreateCustomerFn
  bulkCreateCustomers: BulkCreateCustomersFn
  primeProvider: PrimeProviderFn
  createProDetailsViewModel: PromiseLike<ProDetailsViewModelFactory>
  fetchBillingProfile: FetchBillingProfileFn
  fetchMemberCoupons: FetchMemberCouponsFn
  createDocumentsTabViewModel: PromiseLike<DocumentsTabViewModelFactory>
  fetchJobTotals: FetchJobTotalsFn
  fetchJobTotalStatusAggregations: FetchJobTotalStatusAggregationsFn
  promptCreateJob: PromptCreateJobFn
  createUserJobsTabViewModel: PromiseLike<UserJobsTabViewModelFactory>
  createClientAssignmentViewModel: PromiseLike<ClientAssignmentViewModelFactory>
  createAdvisorClientsTabViewModel: PromiseLike<AdvisorClientsTabViewModelFactory>
  fetchPayoutMethodInfo: FetchPayoutMethodInfoFn
}

/**
 * Features relating to managing the users of the current user's workspaces.
 */
export type UserManagementFeature = ReturnType<typeof UserManagementFeature>

export function UserManagementFeature(options: UserManagementFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) => m.InternalUserManagementFeature(options),
  )

  return {
    // Internal
    internal,
  }
}
